import React from "react";
import { Helmet } from "react-helmet-async";
import localeText from "../../locale";
import { useThemeContext } from "../../contexts/ThemeContext/ThemeContext";
import { detectLangFromPath } from "../../utilities/locale/detectLangFromPath";

const _HREFLANG_MAP = { pt: "pt-BR", en: "en", es: "es", it: "it" };
const _VALID_LANGS = ["pt", "en", "es", "it"];

function SEO({ title, description, image, alt, noIndex }) {
  const { subnetInfo } = useThemeContext();
  const subnetName =
    typeof subnetInfo?.name === "string" && subnetInfo.name.trim() ? subnetInfo.name.trim() : null;
  const brandTitle = subnetName || "Extraconomy";
  const resolvedTitle = title && title !== "Extraconomy" ? title : brandTitle;

  const pathname = typeof window !== "undefined" ? window.location.pathname : "";
  const langFromPath = detectLangFromPath(pathname);
  const hasLangPrefix = Boolean(langFromPath);
  const currentLocale = langFromPath;
  const shouldNoIndex = noIndex || !hasLangPrefix;

  const baseUrl =
    typeof window !== "undefined" ? window.location.origin : "https://extraconomy.com";
  const origin = baseUrl.replace(/\/$/, "");

  const pathAfterLang = pathname.replace(/^\/(pt|en|es|it)/, "");
  const pagePath = pathAfterLang || "";

  const homeUrl = hasLangPrefix ? `${origin}/${currentLocale}` : null;
  const logoUrl = "https://extraconomy.com/assets/icons/web-app-manifest-512x512.png";
  const defaultDescription = description || localeText("empowerYourBusinessDescription");
  const defaultImage = image || "https://extraconomy.com/assets/icons/web-app-manifest-512x512.png";
  const defaultAlt = alt || localeText("empowerYourBusinessDescription");

  const structuredData = hasLangPrefix
    ? {
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "Organization",
            name: brandTitle,
            url: `${origin}/${currentLocale}/`,
            logo: logoUrl,
            sameAs: [
              "https://www.linkedin.com/company/extraconomy/",
              "https://www.instagram.com/extraconomy_official",
              "https://www.facebook.com/profile.php?id=61566025845885",
              "https://www.tiktok.com/@extraconomy",
              "https://x.com/extraconomy",
            ],
          },
          {
            "@type": "WebSite",
            name: brandTitle,
            url: homeUrl,
            potentialAction: {
              "@type": "SearchAction",
              target: `${homeUrl}/shop?search={search_term_string}`,
              "query-input": "required name=search_term_string",
            },
          },
        ],
      }
    : null;

  return (
    <>
      <Helmet
        htmlAttributes={hasLangPrefix ? { lang: _HREFLANG_MAP[currentLocale] } : undefined}
        prioritizeSeoTags
      >
        <title>{resolvedTitle}</title>

        <meta name="description" content={defaultDescription} />

        <meta name="robots" content={shouldNoIndex ? "noindex,nofollow" : "index,follow"} />

        {hasLangPrefix && (
          <link rel="canonical" href={`${origin}/${currentLocale}${pagePath}`} />
        )}

        {hasLangPrefix &&
          _VALID_LANGS.map((l) => (
            <link
              key={l}
              rel="alternate"
              hreflang={_HREFLANG_MAP[l]}
              href={`${origin}/${l}${pagePath}`}
            />
          ))}
        {hasLangPrefix && (
          <link rel="alternate" hreflang="x-default" href={`${origin}/it${pagePath}`} />
        )}

        <meta property="og:title" content={resolvedTitle} />
        <meta property="og:description" content={defaultDescription} />
        {hasLangPrefix && (
          <meta property="og:url" content={`${origin}/${currentLocale}${pagePath}`} />
        )}
        <meta property="og:type" content="website" />
        <meta property="og:image" content={defaultImage} />
        <meta property="og:image:secure_url" content={defaultImage} />
        <meta property="og:image:type" content="image/png" />
        <meta property="og:image:width" content="512" />
        <meta property="og:image:height" content="512" />
        <meta property="og:image:alt" content={defaultAlt} />

        <meta property="fb:app_id" content="818536905907545" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={resolvedTitle} />
        <meta name="twitter:description" content={defaultDescription} />
        <meta name="twitter:image" content={defaultImage} />

        <meta itemProp="name" content={resolvedTitle} />
        <meta itemProp="description" content={defaultDescription} />
        <meta itemProp="image" content={defaultImage} />

        {structuredData && (
          <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
        )}
      </Helmet>
    </>
  );
}

export default SEO;
